/**
 * Densify a route leg into ≤100 Map Matching coordinates.
 * Accepts Valhalla legs (shape string + maneuvers) or pre-normalized
 * { geometry, maneuvers } from normalizeTrip.
 *
 * Portable within src/traffic/.
 */

import { decodeShape } from './polyline'

const MAX_POINTS_DEFAULT = 100
const EARTH_RADIUS_M = 6371000

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value))
}

function toRad(deg) {
  return (deg * Math.PI) / 180
}

export function haversineMeters(from, to) {
  const dLat = toRad(to.lat - from.lat)
  const dLon = toRad(to.lon - from.lon)
  const lat1 = toRad(from.lat)
  const lat2 = toRad(to.lat)
  const hav =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(hav)))
}

function arcLengthMeters(geometry, fromIdx, toIdx) {
  let sum = 0
  for (let i = fromIdx; i < toIdx; i++) {
    sum += haversineMeters(geometry[i], geometry[i + 1])
  }
  return sum
}

function dedupeByShapeIndex(points) {
  const seen = new Set()
  const out = []
  for (const point of points) {
    if (seen.has(point.shapeIndex)) {
      continue
    }
    seen.add(point.shapeIndex)
    out.push(point)
  }
  return out.sort((left, right) => left.shapeIndex - right.shapeIndex)
}

function geometryFromLeg(leg, decodeFn) {
  if (Array.isArray(leg?.geometry) && leg.geometry.length > 0) {
    return leg.geometry.map((point, shapeIndex) => ({
      lon: point.lon,
      lat: point.lat,
      shapeIndex: point.shapeIndex ?? shapeIndex,
    }))
  }
  if (Array.isArray(leg?.coordinates) && leg.coordinates.length > 0) {
    return leg.coordinates.map(([lon, lat], shapeIndex) => ({
      lon,
      lat,
      shapeIndex,
    }))
  }
  if (leg?.shape) {
    const lonLat = decodeFn
      ? (() => {
          // Legacy decodeFn returns [lat, lng]
          const decoded = decodeFn(leg.shape, 6)
          if (
            Array.isArray(decoded) &&
            decoded[0] &&
            Math.abs(decoded[0][0]) <= 90 &&
            Math.abs(decoded[0][1]) > 90
          ) {
            return decoded.map(([lat, lng]) => [lng, lat])
          }
          return decoded
        })()
      : decodeShape(leg.shape)
    return (lonLat || []).map(([lon, lat], shapeIndex) => ({
      lon,
      lat,
      shapeIndex,
    }))
  }
  return []
}

/**
 * @param {object} leg Valhalla leg or normalized leg
 * @param {{ maxPoints?: number, decodeFn?: Function }} [options]
 */
export function densifyRoutePoints(leg, options = {}) {
  const maxPoints = options.maxPoints ?? MAX_POINTS_DEFAULT
  const geometry = geometryFromLeg(leg, options.decodeFn)

  if (!geometry.length) {
    return {
      ok: false,
      code: 'missing_shape',
      message: 'Leg has no geometry/shape',
      coordinates: [],
      points: [],
      coordinateCount: 0,
      seedCount: 0,
      geometry: [],
    }
  }

  const seedIndexSet = new Set()
  seedIndexSet.add(0)
  seedIndexSet.add(geometry.length - 1)

  const maneuvers = Array.isArray(leg.maneuvers) ? leg.maneuvers : []
  for (const mnv of maneuvers) {
    if (typeof mnv.begin_shape_index === 'number') {
      seedIndexSet.add(clamp(mnv.begin_shape_index, 0, geometry.length - 1))
    }
  }
  if (maneuvers.length > 0) {
    const last = maneuvers[maneuvers.length - 1]
    if (typeof last.end_shape_index === 'number') {
      seedIndexSet.add(clamp(last.end_shape_index, 0, geometry.length - 1))
    }
  }

  const points = dedupeByShapeIndex(
    [...seedIndexSet].map((shapeIndex) => ({
      lon: geometry[shapeIndex].lon,
      lat: geometry[shapeIndex].lat,
      shapeIndex,
    }))
  )
  const seedCount = points.length

  if (seedCount > maxPoints) {
    return {
      ok: false,
      code: 'too_many_coordinates',
      message: `Route has ${seedCount} maneuver/seed points (max ${maxPoints})`,
      coordinates: points.map((point) => [point.lon, point.lat]),
      points,
      coordinateCount: seedCount,
      seedCount,
      geometry,
    }
  }

  while (points.length < maxPoints) {
    let bestArc = -1
    let bestPairIdx = -1
    let bestMid = null

    for (let i = 0; i < points.length - 1; i++) {
      const startIdx = points[i].shapeIndex
      const endIdx = points[i + 1].shapeIndex
      if (endIdx - startIdx <= 1) {
        continue
      }
      const arc = arcLengthMeters(geometry, startIdx, endIdx)
      if (arc > bestArc) {
        bestArc = arc
        bestPairIdx = i
        let mid = Math.floor((startIdx + endIdx) / 2)
        if (mid <= startIdx) {
          mid = startIdx + 1
        }
        if (mid >= endIdx) {
          mid = endIdx - 1
        }
        bestMid = mid
      }
    }

    if (bestPairIdx < 0 || bestMid == null) {
      break
    }

    points.splice(bestPairIdx + 1, 0, {
      lon: geometry[bestMid].lon,
      lat: geometry[bestMid].lat,
      shapeIndex: bestMid,
    })
  }

  return {
    ok: true,
    coordinates: points.map((point) => [point.lon, point.lat]),
    points,
    coordinateCount: points.length,
    seedCount,
    geometry,
  }
}
